import React, { useMemo } from "react"
import FunnelChart, {
  FunnelStage,
} from "~/ui-rtk/components/charts/FunnelChart/FunnelChart"
import type { HomepageFunnelData } from "../connect"
import { Loader } from "~/ui-rtk/components/ui/common"
import BenchmarkStatCard from "~/ui-rtk/components/ui/charts/BenchmarkStatCard"
import { FunnelBenchmarks, type PlottedRow } from "../../types"

export interface ConversionFlowStep {
  label: string
  value: number
  conversionRate?: number
  abandonmentRate?: number
  lostCount?: number
}

interface HomepageFunnelChartProps {
  data: HomepageFunnelData | null
  compareData?: HomepageFunnelData | null
  isLoading?: boolean
  benchmarks?: FunnelBenchmarks | null
  showBenchmarks?: boolean
  plottedRows?: PlottedRow[]
}

export default function HomepageFunnelChart({
  data,
  compareData,
  isLoading = false,
  benchmarks,
  showBenchmarks = false,
  plottedRows = [],
}: HomepageFunnelChartProps) {
  const funnelStages = useMemo<FunnelStage[]>(() => {
    if (!data) {
      return [
        {
          id: "homepage",
          label: "Homepage Viewers",
          value: 0,
          color: "#EB89B5",
        },
        {
          id: "shopping",
          label: "Shopping Page Viewers",
          value: 0,
          color: "#EB89B5",
        },
        {
          id: "cart",
          label: "Adders to Cart",
          value: 0,
          color: "#EB89B5",
        },
        {
          id: "purchase",
          label: "Purchasers",
          value: 0,
          color: "#EB89B5",
        },
      ]
    }

    return [
      {
        id: "homepage",
        label: "Homepage Viewers",
        value: data.homepageViewers,
        color: "#EB89B5",
      },
      {
        id: "shopping",
        label: "Shopping Page Viewers",
        value: data.shoppingPageViewers,
        color: "#EB89B5",
      },
      {
        id: "cart",
        label: "Adders to Cart",
        value: data.addersToCart,
        color: "#EB89B5",
      },
      {
        id: "purchase",
        label: "Purchasers",
        value: data.purchasers,
        color: "#EB89B5",
      },
    ]
  }, [data])

  const compareFunnelStages = useMemo<FunnelStage[] | undefined>(() => {
    if (!compareData) return undefined

    return [
      {
        id: "homepage",
        label: "Homepage Viewers",
        value: compareData.homepageViewers,
        color: "#EB89B5",
      },
      {
        id: "shopping",
        label: "Shopping Page Viewers",
        value: compareData.shoppingPageViewers,
        color: "#EB89B5",
      },
      {
        id: "cart",
        label: "Adders to Cart",
        value: compareData.addersToCart,
        color: "#EB89B5",
      },
      {
        id: "purchase",
        label: "Purchasers",
        value: compareData.purchasers,
        color: "#EB89B5",
      },
    ]
  }, [compareData])

  // Conversion flow steps for the bottom summary
  const conversionFlowSteps = useMemo<ConversionFlowStep[]>(() => {
    if (!data) return []

    return [
      {
        label: "Homepage Viewers",
        value: data.homepageViewers,
      },
      {
        label: "Shopping Page Viewers",
        value: data.shoppingPageViewers,
        conversionRate: data.homepageToShopConversion,
        abandonmentRate: 100 - data.homepageToShopConversion,
        lostCount: data.homepageViewers - data.shoppingPageViewers,
      },
      {
        label: "Adders to Cart",
        value: data.addersToCart,
        conversionRate: data.shopToCartConversion,
        abandonmentRate: 100 - data.shopToCartConversion,
        lostCount: data.shoppingPageViewers - data.addersToCart,
      },
      {
        label: "Purchasers",
        value: data.purchasers,
        conversionRate: data.cartToPurchaseConversion,
        abandonmentRate: 100 - data.cartToPurchaseConversion,
        lostCount: data.addersToCart - data.purchasers,
      },
    ]
  }, [data])

  // Compute plotted-row-based conversion rates when rows are plotted
  // Homepage stages: [0] Homepage Viewers, [1] Shopping Page Viewers, [2] Adders to Cart, [3] Purchasers
  const plottedStats = useMemo(() => {
    if (plottedRows.length === 0) return null
    const sumHomepage = plottedRows.reduce(
      (s, r) => s + (r.stages[0]?.value ?? 0),
      0,
    )
    const sumShopping = plottedRows.reduce(
      (s, r) => s + (r.stages[1]?.value ?? 0),
      0,
    )
    const sumCart = plottedRows.reduce(
      (s, r) => s + (r.stages[2]?.value ?? 0),
      0,
    )
    const sumPurchases = plottedRows.reduce(
      (s, r) => s + (r.stages[3]?.value ?? 0),
      0,
    )
    return {
      homepageToShop: sumHomepage > 0 ? (sumShopping / sumHomepage) * 100 : 0,
      shopToCart: sumShopping > 0 ? (sumCart / sumShopping) * 100 : 0,
      cartToPurchase: sumCart > 0 ? (sumPurchases / sumCart) * 100 : 0,
      homepageToCart: sumHomepage > 0 ? (sumCart / sumHomepage) * 100 : 0,
      shopToPurchase: sumShopping > 0 ? (sumPurchases / sumShopping) * 100 : 0,
      homepageToPurchase:
        sumHomepage > 0 ? (sumPurchases / sumHomepage) * 100 : 0,
    }
  }, [plottedRows])

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader size={50} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Primary Conversion Rate Cards (with benchmarks) */}
      <div className="grid grid-cols-3 gap-4">
        <BenchmarkStatCard
          label="Homepage → Shop"
          currentValue={
            plottedStats?.homepageToShop ?? data?.homepageToShopConversion ?? 0
          }
          abandonRate={
            plottedStats
              ? 100 - plottedStats.homepageToShop
              : 100 - (data?.homepageToShopConversion ?? 0)
          }
          benchmark={benchmarks?.homepage_to_shopping}
          showBenchmark={showBenchmarks && !plottedStats}
          valueColor="green"
        />
        <BenchmarkStatCard
          label="Shop → Cart"
          currentValue={
            plottedStats?.shopToCart ?? data?.shopToCartConversion ?? 0
          }
          abandonRate={
            plottedStats
              ? 100 - plottedStats.shopToCart
              : 100 - (data?.shopToCartConversion ?? 0)
          }
          benchmark={benchmarks?.shopping_to_cart}
          showBenchmark={showBenchmarks && !plottedStats}
          valueColor="green"
        />
        <BenchmarkStatCard
          label="Cart → Purchase"
          currentValue={
            plottedStats?.cartToPurchase ?? data?.cartToPurchaseConversion ?? 0
          }
          abandonRate={
            plottedStats
              ? 100 - plottedStats.cartToPurchase
              : 100 - (data?.cartToPurchaseConversion ?? 0)
          }
          benchmark={benchmarks?.cart_to_purchase}
          showBenchmark={showBenchmarks && !plottedStats}
          valueColor="green"
        />
      </div>

      {/* Additional Conversion Rates (with benchmarks) */}
      <div className="grid grid-cols-3 gap-4">
        <BenchmarkStatCard
          label="Homepage → Cart"
          currentValue={
            plottedStats?.homepageToCart ?? data?.homepageToCartConversion ?? 0
          }
          abandonRate={
            plottedStats
              ? 100 - plottedStats.homepageToCart
              : 100 - (data?.homepageToCartConversion ?? 0)
          }
          benchmark={benchmarks?.homepage_to_cart}
          showBenchmark={showBenchmarks && !plottedStats}
          valueColor="green"
        />
        <BenchmarkStatCard
          label="Shop → Purchase"
          currentValue={
            plottedStats?.shopToPurchase ?? data?.shopToPurchaseConversion ?? 0
          }
          abandonRate={
            plottedStats
              ? 100 - plottedStats.shopToPurchase
              : 100 - (data?.shopToPurchaseConversion ?? 0)
          }
          benchmark={benchmarks?.shopping_to_purchase}
          showBenchmark={showBenchmarks && !plottedStats}
          valueColor="green"
        />
        <BenchmarkStatCard
          label="Homepage → Purchase"
          currentValue={
            plottedStats?.homepageToPurchase ??
            data?.homepageToPurchaseConversion ??
            0
          }
          abandonRate={
            plottedStats
              ? 100 - plottedStats.homepageToPurchase
              : 100 - (data?.homepageToPurchaseConversion ?? 0)
          }
          benchmark={benchmarks?.homepage_to_purchase}
          showBenchmark={showBenchmarks && !plottedStats}
          valueColor="green"
        />
      </div>

      {/* Funnel Chart */}
      <div className="mt-8">
        <FunnelChart
          stages={funnelStages}
          compareStages={compareFunnelStages}
          plottedRows={plottedRows}
        />

        {/* Legend for plotted rows */}
        {plottedRows.length > 0 && (
          <div className="flex flex-wrap items-center gap-6 mt-4 px-2">
            {plottedRows.map((row, idx) => (
              <div
                key={row.id || `legend-${idx}`}
                className="flex items-center gap-2"
              >
                <div
                  className="w-8 h-2 rounded"
                  style={{ backgroundColor: row.color }}
                />
                <span className="text-sm text-system-gray-400">
                  {row.label}
                </span>
              </div>
            ))}
            {compareFunnelStages && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-1 rounded border-t-2 border-dashed border-white" />
                <span className="text-sm text-system-gray-400">
                  Comparison Period
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Conversion Flow Summary */}
      {conversionFlowSteps.length > 0 && (
        <div className="flex items-center justify-around mt-6 text-sm">
          {conversionFlowSteps.map((step, index) => {
            const nextStep = conversionFlowSteps[index + 1]
            return (
              <React.Fragment key={`flow-step-${index}`}>
                {/* Step */}
                <div className="text-center flex-1">
                  <div className="font-semibold text-white text-lg">
                    {step.value.toLocaleString()}
                  </div>
                  <div className="text-system-gray-400">{step.label}</div>
                </div>

                {/* Arrow between steps */}
                {index < conversionFlowSteps.length - 1 && nextStep && (
                  <div className="text-center px-4">
                    <div className="text-white text-xl">→</div>
                    {nextStep.conversionRate !== undefined && (
                      <div className="text-xs text-green-400 font-semibold mt-1">
                        {nextStep.conversionRate.toFixed(1)}% conversion
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}
